import React, { useEffect, useState } from 'react';
import {
    Box,
    SimpleGrid,
    Heading,
    Text,
    Card,
    CardBody,
    Badge,
    VStack,
    HStack,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Container,
    useColorModeValue,
    Spinner,
    Center
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { Persona } from '../types';
import personaServices from '../services/PersonaServices';
import sessionManager from '../utils/SessionManager';
import { generateSystemMessage, capitalizeFirstChar } from '../utils/systemMessageGenerator';

const PersonaSelectionPage = () => {
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.600', 'gray.300');

    useEffect(() => {
        const fetchPersonas = async () => {
            try {
                setLoading(true);
                const fetchedPersonas = await personaServices.getPersonas('3', 10);
                setPersonas(fetchedPersonas);
                setFilteredPersonas(fetchedPersonas);
            } catch (error) {
                console.error('Error fetching personas:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPersonas();
    }, []);

    useEffect(() => {
        const filtered = personas.filter(persona => {
            const description = getPersonaDescription(persona);
            return persona.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   (persona.provider && persona.provider.toLowerCase().includes(searchTerm.toLowerCase()));
        });
        setFilteredPersonas(filtered);
    }, [searchTerm, personas]);

    const getSessionCount = (personaId: string): number => {
        const sessions = sessionManager.getSessions(personaId);
        return sessions.length;
    };

    const handlePersonaSelect = (persona: Persona) => {
        navigate(`/persona/${persona.id}?bootstrap_version=${persona.bootstrap_version}`);
    };

    const handleQuickChat = (persona: Persona) => {
        const session = sessionManager.createSession(persona.id);
        navigate(`/chat/${persona.id}/${session.id}?bootstrap_version=${persona.bootstrap_version}`);
    };

    const getPersonaDescription = (persona: Persona): string => {
        return persona.desc || 'No description available';
    };

    if (loading) {
        return (
            <Container maxW="container.xl" py={8}>
                <Center>
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" />
                        <Text>Loading personas...</Text>
                    </VStack>
                </Center>
            </Container>
        );
    }

    return (
        <Container maxW="container.xl" py={8}>
            <VStack spacing={8} align="stretch">
                <Box textAlign="center">
                    <Heading size="xl" mb={4}>
                        Choose Your AI Persona
                    </Heading>
                    <Text fontSize="lg" color={textColor}>
                        Select a persona to start a conversation or continue previous sessions
                    </Text>
                </Box>

                <InputGroup maxW="md" mx="auto">
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search personas..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                {filteredPersonas.length === 0 ? (
                    <Center py={8}>
                        <Text fontSize="lg" color={textColor}>
                            {searchTerm ? 'No personas found matching your search.' : 'No personas available.'}
                        </Text>
                    </Center>
                ) : (
                    <SimpleGrid columns={[1, 2, 3]} spacing={6}>
                        {filteredPersonas.map((persona) => {
                            const sessionCount = getSessionCount(persona.id);
                            return (
                                <Card
                                    key={persona.id}
                                    bg={cardBg}
                                    borderColor={borderColor}
                                    borderWidth="1px"
                                    _hover={{
                                        transform: 'translateY(-2px)',
                                        boxShadow: 'lg',
                                        transition: 'all 0.2s'
                                    }}
                                    cursor="pointer"
                                    onClick={() => handlePersonaSelect(persona)}
                                >
                                    <CardBody>
                                        <VStack align="stretch" spacing={4}>
                                            <HStack justify="space-between" align="start">
                                                <Box flex={1}>
                                                    <Heading size="md" mb={2}>
                                                        {capitalizeFirstChar(persona.name) || 'Unnamed Persona'}
                                                    </Heading>
                                                    <Badge
                                                        colorScheme="blue"
                                                        variant="subtle"
                                                        fontSize="xs"
                                                    >
                                                        {persona.provider}
                                                    </Badge>
                                                </Box>
                                                {sessionCount > 0 && (
                                                    <Badge
                                                        colorScheme="green"
                                                        variant="solid"
                                                        borderRadius="full"
                                                    >
                                                        {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                                                    </Badge>
                                                )}
                                            </HStack>

                                            <Text
                                                fontSize="sm"
                                                color={textColor}
                                                noOfLines={3}
                                                minH="60px"
                                            >
                                                {getPersonaDescription(persona)}
                                            </Text>

                                            <HStack spacing={2}>
                                                <Button
                                                    size="sm"
                                                    colorScheme="blue"
                                                    variant="solid"
                                                    flex={1}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleQuickChat(persona);
                                                    }}
                                                >
                                                    Quick Chat
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    flex={1}
                                                    onClick={() => handlePersonaSelect(persona)}
                                                >
                                                    View Sessions
                                                </Button>
                                            </HStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </SimpleGrid>
                )}
            </VStack>
        </Container>
    );
};

export default PersonaSelectionPage;